import { useState, useEffect } from 'react';
import { idibuApi } from '../services/idibu.api';

export interface CountryOption {
    code: string;
    label: string;
}

const FALLBACK: CountryOption[] = [
    { code: 'GB', label: 'United Kingdom' },
    { code: 'US', label: 'United States' },
    { code: 'AU', label: 'Australia' },
    { code: 'CA', label: 'Canada' },
    { code: 'IE', label: 'Ireland' },
    { code: 'DE', label: 'Germany' },
    { code: 'FR', label: 'France' },
    { code: 'NL', label: 'Netherlands' },
    { code: 'ES', label: 'Spain' },
    { code: 'IT', label: 'Italy' },
    { code: 'IN', label: 'India' },
    { code: 'SI', label: 'Singapore' },
    { code: 'HK', label: 'Hong Kong' },
    { code: 'MY', label: 'Malaysia' },
    { code: 'TH', label: 'Thailand' },
    { code: 'PH', label: 'Philippines' },
    { code: 'ID', label: 'Indonesia' },
    { code: 'VN', label: 'Vietnam' },
    { code: 'TW', label: 'Taiwan' },
    { code: 'KR', label: 'South Korea' },
    { code: 'JP', label: 'Japan' },
    { code: 'CN', label: 'China' },
    { code: 'BD', label: 'Bangladesh' },
    { code: 'LK', label: 'Sri Lanka' },
    { code: 'NP', label: 'Nepal' },
    { code: 'PK', label: 'Pakistan' },
    { code: 'BN', label: 'Brunei' },
    { code: 'NZ', label: 'New Zealand' },
    { code: 'UE', label: 'UAE' },
    { code: 'QA', label: 'Qatar' },
    { code: 'SA', label: 'Saudi Arabia' },
    { code: 'ZA', label: 'South Africa' },
    { code: 'NG', label: 'Nigeria' },
    { code: 'BR', label: 'Brazil' },
    { code: 'MX', label: 'Mexico' },
    { code: 'ZZ', label: 'Rest of the World' },
];

let cached: CountryOption[] | null = null;
let inflight: Promise<CountryOption[]> | null = null;

async function fetchCountries(): Promise<CountryOption[]> {
    if (cached) return cached;

    if (!inflight) {
        inflight = idibuApi
            .getPortalCountries()
            .then(({ data }) => {
                const list = (data.countries || []).map((c) => ({
                    code: c.code,
                    label: c.name,
                }));
                cached = list.length > 0 ? list : null;
                return cached || FALLBACK;
            })
            .catch(() => {
                return FALLBACK;
            })
            .finally(() => {
                inflight = null;
            });
    }

    return inflight;
}

/**
 * Fetches the Idibu country list from the API once, then caches it
 * in memory for the lifetime of the page. Shared across all consumers.
 */
export function useIdibuCountries() {
    const [countries, setCountries] = useState<CountryOption[]>(cached || []);
    const [loading, setLoading] = useState(!cached);

    useEffect(() => {
        if (cached) {
            setCountries(cached);
            setLoading(false);
            return;
        }

        let cancelled = false;
        fetchCountries().then((list) => {
            if (!cancelled) {
                setCountries(list);
                setLoading(false);
            }
        });

        return () => { cancelled = true; };
    }, []);

    return { countries, loading };
}
